// Weakness Forecasting — predicts domain failure probability and triggers preemptive reinforcement
// Multi-cert: reads cert-scoped data via certPath(), uses certSpec for domain weights

import { certPath, read } from './firebase-store.js';
import { getRetrievability } from './scheduler.js';
import { isGraphReady, computeCoverage, getWeakConcepts } from './knowledge-graph.js';
import {
  DEFAULT_DOMAIN_WEIGHTS,
  FORECAST_WEIGHT_IRT, FORECAST_WEIGHT_BKT, FORECAST_WEIGHT_FSRS, FORECAST_WEIGHT_KG,
  REINFORCEMENT_THRESHOLD
} from './constants.js';

/**
 * Compute per-domain failure probability (cert-scoped).
 *
 * @param {string} userId
 * @param {string} certId
 * @param {object} certSpec - { domainWeights: { 1: w1, 2: w2, ... } }
 * @returns {Promise<Object>} { domains, weakestDomain, recommendations }
 */
export async function forecastWeaknesses(userId, certId, certSpec) {
  // Load cert-scoped data
  const [theta, mastery, cards] = await Promise.all([
    read(certPath(userId, certId, 'profile/theta')).catch(() => null),
    read(certPath(userId, certId, 'mastery')).catch(() => null),
    read(certPath(userId, certId, 'cards')).catch(() => null)
  ]);

  const thetaData = theta || {};
  const masteryData = mastery || {};
  const cardData = cards || {};

  // Knowledge graph coverage (cert-scoped)
  let coverageData = null;
  let weakConcepts = [];
  if (isGraphReady(certId)) {
    coverageData = computeCoverage(certId, masteryData);
    try {
      weakConcepts = await getWeakConcepts(certId, userId);
    } catch (e) { /* non-fatal */ }
  }

  const certWeights = (certSpec && certSpec.domainWeights) || DEFAULT_DOMAIN_WEIGHTS;
  const domainKeys = Object.keys(certWeights).map(Number);

  const domains = {};
  let weakestDomain = null;
  let highestFailure = 0;

  for (const d of domainKeys) {
    const key = 'd' + d;
    const t = thetaData[d]?.theta ?? thetaData[key]?.theta ?? 0;

    // 1. Theta-based pass probability (logistic IRT)
    const passProb = 1 / (1 + Math.exp(-t));
    const thetaFailure = 1 - passProb;

    // 2. Coverage gap
    let coverageGap = 0.5;
    if (coverageData && coverageData.domains && coverageData.domains[d]) {
      coverageGap = 1 - coverageData.domains[d].coverage;
    }

    // 3. Card decay
    let decayPenalty = 0;
    let domainCards = 0;
    let decayedCards = 0;
    for (const [qid, card] of Object.entries(cardData)) {
      const r = getRetrievability(card);
      if (r !== null && r < 0.5) {
        decayedCards++;
      }
      domainCards++;
    }
    if (domainCards > 0) {
      decayPenalty = decayedCards / domainCards;
    }

    // 4. Weak concepts
    const domainWeakConcepts = weakConcepts.filter(wc => {
      return wc.mastery < 0.4;
    });

    // Combine factors
    const failureProbability = Math.min(1, Math.max(0,
      FORECAST_WEIGHT_IRT * thetaFailure +
      FORECAST_WEIGHT_BKT * coverageGap +
      FORECAST_WEIGHT_FSRS * decayPenalty +
      FORECAST_WEIGHT_KG * Math.min(1, domainWeakConcepts.length / 5)
    ));

    const needsReinforcement = failureProbability > REINFORCEMENT_THRESHOLD;

    domains[d] = {
      failureProbability: Math.round(failureProbability * 100) / 100,
      passEstimate: Math.round((1 - failureProbability) * 100) / 100,
      needsReinforcement,
      factors: {
        thetaFailure: Math.round(thetaFailure * 100) / 100,
        coverageGap: Math.round(coverageGap * 100) / 100,
        decayPenalty: Math.round(decayPenalty * 100) / 100,
        weakConceptCount: domainWeakConcepts.length
      },
      weight: certWeights[d]
    };

    if (failureProbability > highestFailure) {
      highestFailure = failureProbability;
      weakestDomain = d;
    }
  }

  // Generate recommendations
  const recommendations = [];
  for (const [d, data] of Object.entries(domains)) {
    if (data.needsReinforcement) {
      if (data.factors.coverageGap > 0.5) {
        recommendations.push({
          domain: parseInt(d),
          action: 'expand_coverage',
          reason: 'Domain ' + d + ' has ' + Math.round(data.factors.coverageGap * 100) + '% uncovered skills'
        });
      } else if (data.factors.decayPenalty > 0.3) {
        recommendations.push({
          domain: parseInt(d),
          action: 'review_overdue',
          reason: 'Domain ' + d + ' has significant card decay — schedule review session'
        });
      } else {
        recommendations.push({
          domain: parseInt(d),
          action: 'intensive_practice',
          reason: 'Domain ' + d + ' predicted failure at ' + Math.round(data.failureProbability * 100) + '%'
        });
      }
    }
  }

  return {
    domains,
    weakestDomain,
    reinforcementThreshold: REINFORCEMENT_THRESHOLD,
    recommendations: recommendations.sort((a, b) =>
      domains[b.domain].failureProbability - domains[a.domain].failureProbability
    )
  };
}

/**
 * Get the domain that needs the most reinforcement (cert-scoped).
 *
 * @param {string} userId
 * @param {string} certId
 * @param {object} certSpec
 * @returns {Promise<{domain: number, failureProbability: number}|null>}
 */
export async function getReinforcementTarget(userId, certId, certSpec) {
  const forecast = await forecastWeaknesses(userId, certId, certSpec);
  if (!forecast.weakestDomain) return null;

  const weakest = forecast.domains[forecast.weakestDomain];
  if (!weakest.needsReinforcement) return null;

  return {
    domain: forecast.weakestDomain,
    failureProbability: weakest.failureProbability,
    action: forecast.recommendations[0]?.action || 'intensive_practice'
  };
}
